import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Heart, 
  Users, 
  Globe, 
  Sprout, 
  BookOpen,
  Shield,
  Home,
  Leaf,
  Star,
  Target,
  Award
} from "lucide-react";

const FoundationPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    initial: {},
    animate: { transition: { staggerChildren: 0.1 } }
  };

  const programs = [
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Educational Infrastructure",
      description: "Building schools and learning centers in underserved communities worldwide, focusing on financial literacy and entrepreneurship."
    },
    {
      icon: <Home className="w-8 h-8" />,
      title: "Wellness Centers",
      description: "Establishing holistic wellness facilities that combine mental health, physical fitness, and spiritual growth in one space."
    },
    {
      icon: <Leaf className="w-8 h-8" />,
      title: "Land Stewardship",
      description: "Sustainable land management projects that regenerate ecosystems while creating educational and retreat opportunities."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Youth Empowerment",
      description: "Mentorship programs connecting young people with successful entrepreneurs and providing startup capital for their ventures."
    }
  ];

  const impacts = [
    { number: "1,247", label: "Lives Transformed" },
    { number: "23", label: "Communities Served" },
    { number: "8", label: "Countries Active" },
    { number: "$2.8M", label: "Impact Investment" }
  ];

  const giveaways = [
    {
      title: "Land Legacy Giveaway",
      description: "Win 5 acres of developed land with sustainable infrastructure",
      value: "$150,000",
      entries: "2,847",
      deadline: "30 days"
    },
    {
      title: "Education Scholarship Fund",
      description: "Full entrepreneurship education program + mentorship",
      value: "$25,000",
      entries: "1,234",
      deadline: "45 days"
    },
    {
      title: "Wellness Retreat Package",
      description: "7-day transformative experience at our flagship center",
      value: "$5,000",
      entries: "5,691",
      deadline: "15 days"
    }
  ];

  return (
    <div className="bg-black text-white min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Sacred Geometry Background */}
        <div className="absolute inset-0 opacity-8">
          <motion.div 
            className="absolute inset-0"
            animate={{ rotate: -360 }}
            transition={{ duration: 240, repeat: Infinity, ease: "linear" }}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='150' height='150' viewBox='0 0 150 150' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23f59e0b' stroke-opacity='0.1' stroke-width='1'%3E%3Ccircle cx='75' cy='75' r='60'/%3E%3Ccircle cx='75' cy='75' r='40'/%3E%3Ccircle cx='75' cy='75' r='20'/%3E%3Cpath d='M75 15v120M15 75h120'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '150px 150px'
            }}
          />
        </div>

        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/9886973/pexels-photo-9886973.jpeg"
            alt="Community and wellness"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-green-900/40"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div 
              className="flex justify-center mb-8"
              variants={fadeInUp}
            >
              <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-yellow-500 rounded-full flex items-center justify-center shadow-2xl shadow-green-400/50">
                <Heart className="w-12 h-12 text-black" />
              </div>
            </motion.div>

            <motion.h1 
              className="text-6xl md:text-8xl font-bold mb-6"
              variants={fadeInUp}
            >
              Nhalege Wealth <span className="text-green-400">Foundation</span>
            </motion.h1>
            
            <motion.p 
              className="text-2xl md:text-3xl text-green-400 mb-8 font-light"
              variants={fadeInUp}
            >
              The Nonprofit Revolution
            </motion.p>
            
            <motion.p 
              className="text-xl text-white/80 max-w-4xl mx-auto mb-12 leading-relaxed"
              variants={fadeInUp}
            >
              Land-backed wellness, youth empowerment, and global transformation through strategic 
              philanthropy that builds sustainable communities and breaks cycles of limitation.
            </motion.p>

            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center"
              variants={fadeInUp}
            >
              <button className="group bg-gradient-to-r from-green-400 to-yellow-500 text-black px-12 py-4 text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-green-400/40 transition-all duration-300 hover:scale-105">
                <div className="flex items-center space-x-2">
                  <span>Support the Mission</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
              
              <button className="group border-2 border-green-400 text-green-400 px-12 py-4 text-lg font-bold rounded-full hover:bg-green-400 hover:text-black transition-all duration-300 hover:scale-105">
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5" />
                  <span>Enter Giveaway</span>
                </div>
              </button>
              
              <button className="group border-2 border-white/30 text-white px-12 py-4 text-lg font-bold rounded-full hover:bg-white/10 transition-all duration-300 hover:scale-105">
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5" />
                  <span>Partner With Us</span>
                </div>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Impact Stats Section */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {impacts.map((impact, index) => (
              <motion.div 
                key={impact.label}
                className="text-center"
                variants={fadeInUp}
              >
                <div className="text-4xl md:text-5xl font-bold text-green-400 mb-2">
                  {impact.number}
                </div>
                <div className="text-white/70 text-sm font-medium tracking-wider uppercase">
                  {impact.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Core Mission Section */}
      <section className="py-24 bg-black relative">
        <div className="absolute inset-0 opacity-5">
          <img 
            src="https://images.unsplash.com/photo-1480944657103-7fed22359e1d"
            alt="Community building"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-5xl md:text-6xl font-bold mb-8">
                Core <span className="text-green-400">Mission</span>
              </h2>
              <div className="space-y-6 text-lg text-white/80 leading-relaxed">
                <p>
                  The Nhalege Wealth Foundation exists to break generational cycles of limitation 
                  through strategic community investment and sustainable development.
                </p>
                <p>
                  We believe true wealth isn't just financial - it's the freedom to choose your path, 
                  the knowledge to navigate challenges, and the community support to rise together.
                </p>
                <p>
                  Our approach combines <span className="text-green-400 font-semibold">
                  land stewardship, educational infrastructure, and wellness initiatives</span> 
                  to create lasting transformation from the ground up.
                </p>
              </div>
              
              <div className="mt-10 p-8 bg-gradient-to-br from-green-400/10 to-transparent border border-green-400/20 rounded-2xl">
                <h3 className="text-2xl font-bold text-green-400 mb-4">Foundation Principles</h3>
                <ul className="space-y-3 text-white/80">
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span>Sustainable community development over charity</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span>Education and empowerment as core tools</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span>Land-backed initiatives for lasting impact</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span>Holistic wellness and spiritual growth</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div 
              className="relative"
              variants={fadeInUp}
            >
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1608613304899-ea8098577e38"
                  alt="Community impact"
                  className="w-full h-96 object-cover rounded-3xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-3xl"></div>
                
                {/* Floating Impact Card */}
                <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-gray-900 to-black border border-green-400/30 rounded-2xl p-6 backdrop-blur-lg">
                  <div className="flex items-center space-x-4">
                    <Sprout className="w-8 h-8 text-green-400" />
                    <div>
                      <div className="text-2xl font-bold text-white">1,247</div>
                      <div className="text-sm text-white/60">Lives Changed</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Transformation <span className="text-green-400">Programs</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Four pillars of community development and generational change
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {programs.map((program, index) => (
              <motion.div
                key={program.title}
                className="group bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8 hover:border-green-400/50 transition-all duration-500"
                variants={fadeInUp}
                whileHover={{ y: -5 }}
              >
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-yellow-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <span className="text-black">{program.icon}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-green-400 transition-colors duration-300">
                      {program.title}
                    </h3>
                    <p className="text-white/70 leading-relaxed">
                      {program.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Giveaways Section */}
      <section className="py-24 bg-black relative">
        <div className="absolute inset-0 opacity-10">
          <motion.div 
            animate={{ y: [0, -50, 0] }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23f59e0b' fill-opacity='0.1'%3E%3Cpath d='M40 40m-35 0a35 35 0 1 1 70 0a35 35 0 1 1 -70 0'/%3E%3Cpath d='M40 10L65 25v30L40 70L15 55V25z'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '80px 80px'
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Active <span className="text-green-400">Giveaways</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Transforming lives through strategic giveaways that create lasting impact
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {giveaways.map((giveaway, index) => (
              <motion.div
                key={giveaway.title}
                className="group bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-lg border border-green-400/20 rounded-3xl p-8 hover:border-green-400/50 transition-all duration-500 relative overflow-hidden"
                variants={fadeInUp}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                {/* Sacred Geometry Corner */}
                <div className="absolute top-4 right-4 w-6 h-6 border border-green-400/30 rotate-45"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <Award className="w-8 h-8 text-green-400" />
                    <div className="text-3xl font-bold text-green-400">{giveaway.value}</div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-green-400 transition-colors duration-300">
                    {giveaway.title}
                  </h3>
                  
                  <p className="text-white/70 mb-6 leading-relaxed">
                    {giveaway.description}
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">Entries:</span>
                      <span className="text-green-400 font-semibold">{giveaway.entries}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">Deadline:</span>
                      <span className="text-yellow-400 font-semibold">{giveaway.deadline}</span>
                    </div>
                    
                    <button className="w-full bg-gradient-to-r from-green-400 to-yellow-500 text-black py-3 font-bold rounded-full hover:shadow-lg hover:shadow-green-400/30 transition-all duration-300 hover:scale-105">
                      Enter Giveaway
                    </button>
                  </div>
                </div>

                {/* Hover Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-400/0 via-green-400/0 to-green-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"></div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-br from-green-400/10 via-black to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="text-5xl md:text-6xl font-bold mb-8"
              variants={fadeInUp}
            >
              Join the <span className="text-green-400">Revolution</span>
            </motion.h2>
            
            <motion.p 
              className="text-xl text-white/80 mb-12"
              variants={fadeInUp}
            >
              Be part of the movement that transforms communities and builds generational wealth through strategic giving.
            </motion.p>
            
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 gap-8"
              variants={fadeInUp}
            >
              <div className="bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-green-400/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-green-400 mb-6">Support the Mission</h3>
                <div className="space-y-4">
                  <button className="w-full bg-gradient-to-r from-green-400 to-yellow-500 text-black py-3 font-bold rounded-full hover:shadow-lg hover:shadow-green-400/30 transition-all duration-300">
                    Donate $100
                  </button>
                  <button className="w-full bg-gradient-to-r from-green-400 to-yellow-500 text-black py-3 font-bold rounded-full hover:shadow-lg hover:shadow-green-400/30 transition-all duration-300">
                    Donate $500
                  </button>
                  <button className="w-full border-2 border-green-400 text-green-400 py-3 font-bold rounded-full hover:bg-green-400 hover:text-black transition-all duration-300">
                    Custom Amount
                  </button>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-green-400/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-green-400 mb-6">Partnership Inquiry</h3>
                <div className="space-y-4">
                  <input 
                    type="text" 
                    placeholder="Organization Name"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-green-400"
                  />
                  <input 
                    type="email" 
                    placeholder="Contact Email"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-green-400"
                  />
                  <button className="w-full bg-gradient-to-r from-green-400 to-yellow-500 text-black py-3 font-bold rounded-full hover:shadow-lg hover:shadow-green-400/30 transition-all duration-300">
                    Start Partnership
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Navigation Footer */}
      <section className="py-16 bg-gray-900 border-t border-green-400/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link 
              to="/capital"
              className="group flex items-center space-x-2 text-white/70 hover:text-green-400 transition-colors duration-300 mb-4 md:mb-0"
            >
              <ArrowRight className="w-5 h-5 rotate-180 group-hover:-translate-x-1 transition-transform" />
              <span>Previous: Capital</span>
            </Link>
            
            <Link 
              to="/"
              className="text-green-400 font-semibold hover:text-white transition-colors duration-300"
            >
              ⬟ Empire Overview
            </Link>
            
            <Link 
              to="/media"
              className="group flex items-center space-x-2 text-white/70 hover:text-green-400 transition-colors duration-300 mt-4 md:mt-0"
            >
              <span>Next: Media & Education</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FoundationPage;